import type { MountedComposer } from "./ComposerMountContext";
import type { AttachmentMeta, ComposerMode } from "./useComposerDraft";

/**
 * Shape of a `composer.apply` event delivered over the WS bus.
 *
 * Stage 4 adds `append_text` and `set_attachments`. `applyComposerEvent`
 * drops unknown ops silently so an older frontend deployed against a newer
 * backend does not crash.
 */
export type ComposerApplyEvent =
  | {
      readonly type: "composer.apply";
      readonly mode: ComposerMode;
      readonly thread_key: string;
      readonly revision: number;
      readonly op: "set_text";
      readonly text: string;
    }
  | {
      readonly type: "composer.apply";
      readonly mode: ComposerMode;
      readonly thread_key: string;
      readonly revision: number;
      readonly op: "append_text";
      readonly text: string;
    }
  | {
      readonly type: "composer.apply";
      readonly mode: ComposerMode;
      readonly thread_key: string;
      readonly revision: number;
      readonly op: "set_attachments";
      readonly attachment_ids: readonly string[];
    };

/**
 * Dispatch a `composer.apply` event into the currently-mounted composer.
 *
 * Drops silently when there is no mounted composer or when (mode, thread_key)
 * mismatches. Per INV-15: cross-user isolation is handled upstream by the WS
 * filter; this layer only filters within a user's own tabs to the matching
 * mounted view.
 *
 * Never invokes onSend (INV-10). Only mutates draft state via mounted.applyOp.
 */
export function applyComposerEvent(
  event: ComposerApplyEvent,
  mounted: MountedComposer | null,
  // Provide the current text so `append_text` can concatenate without
  // racing React state. The caller pulls this from the same localStorage
  // hook that `useComposerDraft` reads.
  currentText = "",
  // Current attachment meta so `set_attachments` can keep names for ids
  // that survive the replace and drop meta for ids that were removed.
  // Ids with no prior meta get a placeholder `{id, name: id}` so the
  // chip UI and the outbound payload stay in sync.
  currentAttachmentMeta: readonly AttachmentMeta[] = [],
): void {
  if (!mounted) return;
  if (mounted.mode !== event.mode) return;
  if (mounted.threadKey !== event.thread_key) return;
  switch (event.op) {
    case "set_text":
      mounted.applyOp({ text: event.text, revision: event.revision });
      return;
    case "append_text":
      mounted.applyOp({
        text: currentText + event.text,
        revision: event.revision,
      });
      return;
    case "set_attachments": {
      const metaById = new Map(currentAttachmentMeta.map((m) => [m.id, m]));
      const nextMeta: readonly AttachmentMeta[] = event.attachment_ids.map(
        (id) => metaById.get(id) ?? { id, name: id },
      );
      mounted.applyOp({
        attachments: event.attachment_ids,
        attachmentMeta: nextMeta,
        revision: event.revision,
      });
      return;
    }
    default:
      // Forward-compat: unknown op → drop.
      return;
  }
}
